import { ChatMessage } from "../protocol/messages/chat-message";
import { CuePositionMessage } from "../protocol/messages/cue-position-message";
import { DiscardGameMessage } from "../protocol/messages/discard-game-message";
import { GameAddedMessage } from "../protocol/messages/game-added-message";
import { GameMessage } from "../protocol/messages/game-message";
import { GameRemovedMessage } from "../protocol/messages/game-removed-message";
import { GameUserAddedMessage } from "../protocol/messages/game-user-added-message";
import { GameUserRemovedMessage } from "../protocol/messages/game-user-removed-message";
import { HitCueBallMessage } from "../protocol/messages/hit-cue-ball-message";
import { HitPointMessage } from "../protocol/messages/hit-point-message";
import { HitPowerMessage } from "../protocol/messages/hit-power-message";
import { InviteStatusMessage } from "../protocol/messages/invite-status-message";
import { NewGameInviteMessage } from "../protocol/messages/new-game-invite-message";
import { PassBackMessage } from "../protocol/messages/pass-back-message";
import { PlayerListMessage } from "../protocol/messages/player-list-message";
import { StartGameMessage } from "../protocol/messages/start-game-message";
import { TournamentAcceptResultMessage } from "../protocol/messages/tournament-accept-result-message";
import { TournamentGameMessage } from "../protocol/messages/tournament-game-message";
import { TournamentInviteMessage } from "../protocol/messages/tournament-invite-message";
import { UserAddedMessage } from "../protocol/messages/user-added-message";
import { UserCreditUpdateMessage } from "../protocol/messages/user-credit-update-message";
import { UserDetailsMessage } from "../protocol/messages/user-details-message";
import { UserRemovedMessage } from "../protocol/messages/user-removed-message";
import { UserUpdateMessage } from "../protocol/messages/user-update-message";
import { MutationType } from "../stores/mutation-types";
import { store, useStore } from "../stores/index";
import gameApplication from "../game/game-app";
import { HitDirectionMessage } from "../protocol/messages/hit-direction-message";
import { WebsocketMessageType } from "../protocol/websocket-message-type";
import { TournamentSignupResultMessage } from "../protocol/messages/tournament-signup-result-message";
import { SynchronizeGameMessage } from "../protocol/messages/synchronize-game-message";
import { Vector2 } from "../math/vector2";
import { ErrorMessage } from "../protocol/messages/error-message";
import { MatchEndMessage } from "../protocol/messages/match-end-message";
import { GameActionEnum } from "../types/game-action-enum";

/*
 * Messages from the game server are routed to the "task" methods.
 * These "task" methods are similar to the original Java applet tasks classes.
 * ENTER_ROOM_CONFIRM is not included here as from the Java class as this is done is the REST API.
 */
export class GameTasks {
  /**
   * Original applet task code:
   * AbstractPlayer player = m.getPlayer();
   *
   * if (!player.equals(applet.getPlayer()))
   *    applet.getRoomPanel().addPlayer(player);
   */
  public userDetailsTask(message: UserDetailsMessage): void {
    // do nothing this is currently done via REST and Vue actions
  }

  /**
   * Original applet task code:
   * applet.selectRoomToJoin(m.getRooms());
   */
  public listRoomsTask(message: PlayerListMessage): void {
    // do nothing this is currently done via REST and Vue actions
  }

  /**
   * Original applet task code:
   * RoomPanel roomPanel = applet.getRoomPanel();
   * PlayerListMessage m = (PlayerListMessage) msg;
   *
   * roomPanel.setRoom(applet.selectedRoom);
   * roomPanel.setPlayers(m.getPlayers());
   * roomPanel.setGames(m.getGames());
   */
  public playerListTask(message: PlayerListMessage): void {
    store.commit(MutationType.PLAYER_LIST, message.players);
  }

  /**
   * Original applet task code:
   * if (!player.equals(applet.getPlayer()))
   *    applet.getRoomPanel().addPlayer(player);
   */
  public userAddedTask(message: UserAddedMessage): void {
    store.commit(MutationType.USER_ADDED, message.player);
  }

  /**
   * Original applet task code:
   * applet.getRoomPanel().removePlayer(m.getPlayer());
   */
  public userRemovedTask(message: UserRemovedMessage): void {
    store.commit(MutationType.USER_REMOVED, message.player);
  }

  /**
   * Original applet task code:
   * if (applet.getGameStage() == GameStage.GameInProgress) {
   * 		applet.getGamePanel().addChatMessage(m.getPlayerId(), m.getMessage());
   * } else if (applet.getGameStage() == GameStage.InRoom) {
   *    applet.getRoomPanel().addChatMessage(m.getPlayerId(), m.getMessage());
   * }
   */
  public chatMessageTask(message: ChatMessage): void {
    if (store.state.settings.enableChatMessages) {
      store.commit(MutationType.CHAT_MESSAGE, message);
    }
  }
  
  /**
   * This is shown when the user is accepting or rejecting a game invite.
   *
   * Original applet task code:
   * applet.newGameInvite(m.getGame(), m.isContinuation());
   */
  public newGameInviteTask(message: NewGameInviteMessage): void {
    gameApplication.newGameInvite(message.game, message.continuation);
  }

  /**
   * Original applet task code:
   * applet.gameInviteStatus(m.isInviteSent());
   */
  public inviteStatusTask(message: InviteStatusMessage): void {
    gameApplication.gameInviteStatus(message.inviteSent);
  }

  /**
   * Original applet task code:
   * applet.startGame(game, m.getInitiatorIndex(), m.getSeed1(), m.getSeed2());
   */
  public startGameTask(message: StartGameMessage): void {  
    gameApplication.startGame(
      message.game,
      Number(message.initiatorIndex), // this appears to be always 0 (zero) on the server
      Number(message.seed1),
      Number(message.seed2)
    );
  }

  /**
   * This is for the users remaining in the game room after the game is added.
   *
   * Original applet task code:
   * applet.getRoomPanel().addGame(m.getGame());
   */
  public gameAddedTask(message: GameAddedMessage): void {
    // possibly add a message to the chat
  }

  /**
   * This is for the users remaining in the game room after the game is removed.
   *
   * Original applet task code:
   * applet.getRoomPanel().removeGame(m.getGame());
   */
  public gameRemovedTask(message: GameRemovedMessage): void {
    // possibly add a message to the chat
  }

   /**
   * Original applet task code:
   * applet.getGamePanel().setHitPower(m.getPower());
   */
  public hitPowerAdjustTask(message: HitPowerMessage): void {
    // TODO: Test this method and also send an event to update the front end.
    //gameApplication.getBillardGame().stick.power = Number(message.power);
  }

  /**
   * Original applet task code:
   * applet.getGamePanel().setHitPoint(m.getPosition());
   */
  public hitPointAdjustTask(message: HitPointMessage): void {
    if (message.playerId != gameApplication.getPlayer().playerId) {
      if (gameApplication.getBillardGame().isOpponentTurn()) {
         // Emit "opponent adjust hit position" event to the Vue.js front end.
        gameApplication.fireEvent('OPPONENT_ADJUST_HIT_POSITION', message);
      }
    }
  }

  /**
   * Original applet task code:
   * applet.getGamePanel().setCueDirection(m.getDirection());
   */
  public hitDirectionAdjustTask(message: HitDirectionMessage): void {
    if (message.playerId != gameApplication.getPlayer().playerId) {
      if (!gameApplication.getBillardGame().stick.movable
        && gameApplication.getBillardGame().isOpponentTurn()) {
        const opposite: number = message.directionY;
        const adjacent: number = message.directionX;
        gameApplication.getBillardGame().stick.rotation = Math.atan2(opposite, adjacent);
      }
    }
  }

  /**
   * Original applet task code::
   * GamePanel gamePanel = applet.getGamePanel();
   * gamePanel.setHitPower(m.getPower());
   * gamePanel.setHitPoint(m.getHitPoint());
   * gamePanel.setCueDirection(m.getDirection());
   * applet.getGame().getSimulator().getCueBall().setPosition(m.getBallPosition());
   * try {
   *   gamePanel.hitCueBall(m.isRandom());
   * }
   * catch (GameException ge) {
   *   ge.printStackTrace();
   * }
   */
  public hitCueBallTask(message: HitCueBallMessage): void {
    // only send the hit cue ball event to the Vue.js front end from the other player
    if (message.playerId != gameApplication.getPlayer().playerId) {
      if (gameApplication.getBillardGame().isOpponentTurn()) {
         // Emit "opponent hit cue ball" event to the Vue.js front end.
        gameApplication.fireEvent('OPPONENT_HIT_CUE_BALL', message);
        gameApplication.setSynchronized(false);
      }
    }
  }

  /**
   * Original applet task code:
   * applet.discardGame(m.getSender());
   */
  public discardGameTask(message: DiscardGameMessage): void {
    gameApplication.discardGame(message.player);  
  }

  /**
   * Original applet task code:
   * applet.getGamePanel().addPlayer(m.getPlayer());
   */
  public gameUserAddedTask(message: GameUserAddedMessage): void {
    // do nothing for now
    // possibly add a message to the chat
  }

  /**
   * Original applet task code:
   * applet.getGamePanel().userRemoved(m.getPlayer());
   */
  public gameUserRemovedTask(message: GameUserRemovedMessage): void {
    gameApplication.userRemoved(message.player);
  }

  /**
   * Original applet task code:
   * applet.getGamePanel().setCuePosition(m.getPosition());
   */
  public cueBallPositionTask(message: CuePositionMessage): void {
    // only set the cue ball position if the message is sent from the other player
    if (message.playerId != gameApplication.getPlayer().playerId) {
      if (gameApplication.getBillardGame().isOpponentTurn()) {
        gameApplication.getBillardGame().positionCueBallInHand(new Vector2(Number(message.positionX), Number(message.positionY)));
        gameApplication.getBillardGame().stick.movable = false;  
        gameApplication.getBillardGame().gameState.setupCueBall = false;
      }
    }
  }

  /**
   * Original applet task code:
   * applet.gameActionReceived(m.getAction());
   */
  public gameActionTask(message: GameMessage): void {
    if (message.action == GameActionEnum.PassBack) {
      gameApplication.passBack(message);
    }
  }

  /**
   * Original applet task code:
   * applet.passBack();
   */
  public passBackTask(message: PassBackMessage): void {
    gameApplication.passBack(message);
  }

  /**
   * Add tournament invite link to chat.
   * If the player joins the tournament send a tournament signup message to 
   * the game server.
   * 
   * Original applet task code:
   * applet.getRoomPanel().addTournamentInvite(m.getTournamentId(), m.getMinutes());
   */
  public tournamentInviteTask(message: TournamentInviteMessage): void {
    const store = useStore();

    const chatMessage: ChatMessage = {
      msgType: WebsocketMessageType.CHAT_MSG,
      playerId: 'TOURNAMENT',
      message: message.tournamentId + ' is available to join (' + message.minutes + ' minutes till start) ',
    }
    store.commit(MutationType.CHAT_MESSAGE, chatMessage);
    
    const joinMessage: ChatMessage = {
      msgType: WebsocketMessageType.CHAT_MSG,
      playerId: 'TOURNAMENT',
      message: '<span style="font-weight: bold; text-decoration: underline; cursor: pointer" class="tournament-link" data-tournamentid="' + message.tournamentId + '">Click here to Join!</span>',
    }
    store.commit(MutationType.CHAT_MESSAGE, joinMessage);
  }

  /**
   * Original applet task code:
   * applet.tournamentSignupResult(m.getTournamentRoom(), m.isRegistered());
   */
  public tournamentJoinResultTask(message: TournamentSignupResultMessage): void {
    gameApplication.tournamentSignupResult(message.tournamentRoom, message.registered);
  }

  /**
   * Original applet task code:
   * applet.tournamentAcceptResult(m.getTournamentRoom());
   */
  public tournamentAcceptResultTask(message: TournamentAcceptResultMessage): void {
    gameApplication.tournamentAcceptResult(message.tournamentRoom);
  }

  /**
   * Original applet task code:
   * applet.tournamentStatus(m.getStatus());
   */
  public tournamentJoinStatusTask(message: TournamentGameMessage): void {
    gameApplication.tournamentStatus(message.status);
  }

  /**
   * Original applet task code:
   * applet.getRoomPanel().modifyPlayer(player);
   */
  public userUpdateTask(message: UserUpdateMessage): void {
    // FIXME: THE CODE LINE BELOW SEEMS TO CAUSE ISSUES IN THE CODE. NEED TO INVESTIGATE!!
    // store.commit(MutationType.USER_UPDATED, message.player);
  }
  
  /**
   * Original applet task code:
   * applet.moifyPlayerCredits(credits);
   */
  public userCreditUpdateTask(message: UserCreditUpdateMessage): void {
    gameApplication.moifyPlayerCredits(message.amount);
  }

  /**
   * Original applet task code:
   * applet.initRoomPanel();
   */
  public matchEndTask(message: MatchEndMessage): void {
    // Emit "end game" event to the Vue.js front end.
    gameApplication.fireEvent('END_GAME');
    
    gameApplication.initRoom(); // go back to the room and reinitialise the room data 
  }

  /**
   * Original applet task code:
   * applet.continueGame();
   */
  public synchronizeGameTask(message: SynchronizeGameMessage): void {
    // On the Java version of the game this is done on the server side.
    // The Java version can have watchers that need to be synchronised.
    // On the browser version there are no watchers therefore only the game
    // players need to be synchronised.
    gameApplication.checkPlayersState();

    // wait until the opponents animation is finished to continue the game
    if (gameApplication.isSynchronized()) {
      gameApplication.fireEvent('END_TURN'); // Emit "end turn" event to the Vue.js front end.  
      gameApplication.clearSynchro();
      gameApplication.getBillardGame().continueGame();
    } 
  }

  public errorTask(message: ErrorMessage): void {
    gameApplication.errorGame();
  }
}

const gameTasks = new GameTasks();

export default gameTasks;
